import { Instagram, Youtube, Facebook } from "lucide-react";

export default function Footer() {
  const year = new Date().getFullYear();
  // Scroll to the target section if present on the current page — otherwise
  // navigate to the homepage anchor so Contact / Shop / Blog links work from
  // ANY page (gym, arena, arcade, etc.). Uses `location.assign` so the global
  // hash-anchor scroll effect in AppRouter picks it up after the SPA route.
  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.assign(`/#${id}`);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-black border-t border-zinc-900 pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-10 mb-14">
          <div className="md:col-span-2">
            <div className="inline-block p-0.5 rounded-full border-2 border-[#6D28D9] mb-3 shadow-[0_0_16px_rgba(109,40,217,0.35)] bg-black/40">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_omni-brand-1/artifacts/84dtbvvf_1783524637283.png"
                alt="OMNIMUSCLE phoenix medallion"
                className="block h-14 w-14 rounded-full object-cover"
              />
            </div>
            <div className="mb-5">
              <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#6D28D9]">
                Holistic Healing
              </span>
            </div>
            <p className="text-neutral-400 max-w-md mb-4 leading-relaxed">
              Flying higher to inspire positive change.{" "}
              <span className="text-white">🌈 Mind, Body &amp; Soul 🌈</span>{" "}
              Programs, coaching, and supplement stacks built by an athlete in
              the trenches — for the ones ready to rise.
            </p>
            <div className="flex flex-wrap gap-2 mb-6">
              <span
                data-testid="footer-black-owned"
                className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 border border-[#D4AF37] text-[#D4AF37]"
              >
                Black-Owned
              </span>
              <span
                data-testid="footer-lgbtq-owned"
                className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 border border-[#D4AF37] text-[#D4AF37]"
              >
                🌈 LGBTQ+ Owned
              </span>
              <span
                data-testid="footer-autistic-owned"
                className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 border border-[#D4AF37] text-[#D4AF37]"
              >
                ∞ Autistic-Owned
              </span>
              <span
                data-testid="footer-disabled-owned"
                className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 border border-[#D4AF37] text-[#D4AF37]"
              >
                Disabled-Owned
              </span>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="https://instagram.com/OmniMuscle_Bodybuilding"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="w-11 h-11 border border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://youtube.com/@omnimuscle"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-youtube"
                className="w-11 h-11 border border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white flex items-center justify-center transition-colors"
                aria-label="YouTube"
              >
                <Youtube size={18} />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61589911520560&mibextid=ZbWKwL"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook"
                className="w-11 h-11 border border-zinc-800 hover:border-[#D4AF37] hover:text-[#D4AF37] text-white flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
            </div>
            <a
              data-testid="footer-email"
              href="mailto:CoachOmniMuscle@gmail.com"
              className="mt-3 inline-block text-xs text-neutral-400 hover:text-[#D4AF37] transition-colors tracking-wide"
            >
              ✉ CoachOmniMuscle@gmail.com
            </a>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold mb-4">
              Explore
            </div>
            <ul className="space-y-3 text-neutral-400">
              {[
                ["about", "About"],
                ["programs", "Programs"],
                ["ai-coach", "AI Coach"],
              ].map(([id, label]) => (
                <li key={id}>
                  <button
                    data-testid={`footer-link-${id}`}
                    onClick={() => goTo(id)}
                    className="hover:text-[#D4AF37] transition-colors text-sm"
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-bold mb-4">
              More
            </div>
            <ul className="space-y-3 text-neutral-400">
              {[
                ["shop", "Shop"],
                ["blog", "Blog"],
                ["contact", "Contact"],
              ].map(([id, label]) => (
                <li key={id}>
                  <button
                    data-testid={`footer-link-${id}`}
                    onClick={() => goTo(id)}
                    className="hover:text-[#D4AF37] transition-colors text-sm"
                  >
                    {label}
                  </button>
                </li>
              ))}
              <li>
                <a
                  href="/supporters"
                  data-testid="footer-link-supporters"
                  className="hover:text-[#D4AF37] transition-colors text-sm"
                >
                  Friends &amp; Supporters
                </a>
              </li>
              <li>
                <a
                  href="/faq"
                  data-testid="footer-link-faq"
                  className="hover:text-[#D4AF37] transition-colors text-sm"
                >
                  FAQ
                </a>
              </li>
              <li>
                <a
                  href="/forums"
                  data-testid="footer-link-forums"
                  className="hover:text-[#D4AF37] transition-colors text-sm"
                >
                  Forums 💬
                </a>
              </li>
              <li>
                <a
                  href="/hall-of-phoenix"
                  data-testid="footer-link-hop"
                  className="hover:text-[#D4AF37] transition-colors text-sm"
                >
                  Hall of Phoenix 🏛️
                </a>
              </li>
              <li>
                <a
                  href="/roam"
                  data-testid="footer-link-roam"
                  className="hover:text-[#D4AF37] transition-colors text-sm"
                >
                  Castle Courtyard 🌾
                </a>
              </li>
              <li>
                <a
                  href="/chief-cook/david-lauer"
                  data-testid="footer-link-chef"
                  className="hover:text-[#D4AF37] transition-colors text-sm"
                >
                  Chief Cook 👨‍🍳
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-zinc-900 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-sm text-neutral-500">
            © {year} OMNIMUSCLE. All rights reserved. · Flying Higher 🌈
          </div>
          <div className="text-xs text-neutral-600 uppercase tracking-widest">
            Mind · Body · Soul
          </div>
        </div>
      </div>
    </footer>
  );
}
